import React from "react";
import { Button } from "./ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Catches unhandled render/lifecycle errors so a single bad component
 * (e.g., a malformed image URL or unexpected state after an upload)
 * cannot blank the entire application.
 *
 * Also logs the error to the browser console + posts a lightweight
 * beacon to /api/client-errors so admins can see production issues
 * we cannot reproduce in preview.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error("[ErrorBoundary]", this.props.label || "root", error, info?.componentStack);
        try {
            const beacon = {
                where: this.props.label || "root",
                message: String(error?.message || error),
                stack: String(error?.stack || "").slice(0, 4000),
                component_stack: String(info?.componentStack || "").slice(0, 4000),
                url: typeof window !== "undefined" ? window.location.href : "",
                ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
                ts: new Date().toISOString(),
            };
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/client-errors`;
            if (typeof navigator !== "undefined" && navigator.sendBeacon) {
                const blob = new Blob([JSON.stringify(beacon)], { type: "application/json" });
                navigator.sendBeacon(url, blob);
            } else if (typeof fetch !== "undefined") {
                fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(beacon),
                    credentials: "include",
                    keepalive: true,
                }).catch(() => { /* best-effort */ });
            }
        } catch { /* never let the error handler itself throw */ }
    }

    reset = () => this.setState({ hasError: false, error: null });

    render() {
        if (!this.state.hasError) return this.props.children;
        if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
        return (
            <div className="mx-auto max-w-2xl my-10 p-6 rounded-2xl border border-destructive/40 bg-destructive/5" data-testid="error-boundary-fallback">
                <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <h3 className="font-heading font-bold text-lg">Something went wrong</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    {this.props.label ? `Section: ${this.props.label}. ` : ""}
                    The page hit an unexpected error. Your work isn&apos;t lost — reload to try again.
                </p>
                {this.state.error?.message && (
                    <pre className="text-xs bg-white/60 rounded-lg p-3 border border-destructive/20 overflow-x-auto mb-3">
                        {String(this.state.error.message).slice(0, 500)}
                    </pre>
                )}
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={this.reset} data-testid="error-boundary-retry">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
                    </Button>
                    <Button variant="ghost" size="sm" className="rounded-full" onClick={() => window.location.reload()} data-testid="error-boundary-reload">
                        Reload page
                    </Button>
                </div>
            </div>
        );
    }
}
